import { businessDate, dateTime, money, qty, type FormatSettings } from '../../../lib/format';
import type { ShopSettings } from '../../../shop/ShopProvider';
import { dec, toFixed } from '../lib/decimal';
import type { CounterSale, Delivery, SalesInvoice, SalesOrder } from '../lib/api';
import type { PrintDoc, PrintLine, PrintRow, ReceiptData, ShopHeader } from './documents';

export function shopHeader(
  settings: ShopSettings | null,
  fallbackName = 'Your shop',
  logoSrc: string | null = null,
): ShopHeader {
  const prefs = settings?.printPreferences;
  return {
    name: settings?.name ?? fallbackName,
    address: settings?.address,
    phone: settings?.phone,
    email: settings?.email,
    taxNumber: prefs?.showTaxNumber === false ? null : settings?.taxNumber,
    footerText: prefs?.footerText,
    termsText: prefs?.termsText,
    accent: settings?.themeColor,
    logoSrc,
  };
}

/** A priced document line (sales, quotation and purchase lines all have these). */
export type PricedLine = Pick<
  SalesInvoice['lines'][number] | SalesOrder['lines'][number],
  'description' | 'qty' | 'unitPrice' | 'discountAmount' | 'taxRate' | 'grossAmount'
> & { sku?: string | null; unitSymbol: string | null };
type PricedDoc = Pick<SalesInvoice, 'documentDiscount' | 'taxTotal' | 'total'> & {
  pricesIncludeTax?: boolean;
  lines: PricedLine[];
};

/** Line amount after the line's own discount (before the document discount and tax). */
function lineAmount(l: PricedLine, decimals: number): string {
  return toFixed(dec(l.grossAmount) - dec(l.discountAmount), decimals);
}

export function priceLines(lines: PricedLine[], f: FormatSettings): PrintLine[] {
  return lines.map((l) => {
    const rate = dec(l.taxRate);
    return {
      description: l.description,
      sku: l.sku ?? undefined,
      qty: qty(l.qty, f),
      unit: l.unitSymbol ?? '',
      unitPrice: money(l.unitPrice, f),
      discount: dec(l.discountAmount) > 0n ? `−${money(l.discountAmount, f)}` : '—',
      tax: rate > 0n ? `${qty(l.taxRate, f)}%` : '—',
      amount: money(lineAmount(l, f.currencyDecimals), f),
    };
  });
}

/** Subtotal (Σ line amounts) − document discount + tax = total; the rows add up as printed. */
export function totalRows(d: PricedDoc, f: FormatSettings): PrintRow[] {
  const sub = d.lines.reduce((s, l) => s + dec(lineAmount(l, f.currencyDecimals)), 0n);
  const rows: PrintRow[] = [
    { label: 'Subtotal', value: money(toFixed(sub, f.currencyDecimals), f) },
  ];
  if (dec(d.documentDiscount) > 0n)
    rows.push({ label: 'Discount', value: `−${money(d.documentDiscount, f)}` });
  if (dec(d.taxTotal) > 0n)
    rows.push({
      label: d.pricesIncludeTax ? 'Tax (included)' : 'Tax',
      value: money(d.taxTotal, f),
    });
  rows.push({ label: 'Total', value: money(d.total, f), strong: true });
  return rows;
}

export function invoiceDoc(
  invoice: SalesInvoice,
  f: FormatSettings,
  taxInvoice: boolean,
): PrintDoc {
  const cancelled = invoice.status === 'CANCELLED';
  const lines = priceLines(invoice.lines, f);
  const totals = totalRows(invoice, f);
  if (dec(invoice.amountPaid) > 0n)
    totals.push({ label: 'Paid', value: `−${money(invoice.amountPaid, f)}` });
  return {
    title: taxInvoice ? 'Tax invoice' : 'Invoice',
    number: invoice.number,
    status: cancelled ? 'Cancelled' : invoice.paymentStatus === 'PAID' ? 'Paid' : undefined,
    meta: [
      { label: 'Invoice date', value: businessDate(invoice.docDate, f) },
      { label: 'Due date', value: businessDate(invoice.dueDate, f) },
      ...(invoice.reference ? [{ label: 'Reference', value: invoice.reference }] : []),
    ],
    party: { heading: 'Bill to', name: invoice.contact.name, phone: invoice.contact.phone },
    lines,
    showPrices: true,
    totals,
    balance: cancelled ? undefined : { label: 'Balance due', value: money(invoice.outstanding, f) },
    notes: invoice.notes,
  };
}

export function orderDoc(order: SalesOrder, f: FormatSettings): PrintDoc {
  return {
    title: 'Sales order',
    number: order.number,
    meta: [
      { label: 'Order date', value: businessDate(order.docDate, f) },
      ...(order.expectedDate
        ? [{ label: 'Expected', value: businessDate(order.expectedDate, f) }]
        : []),
      ...(order.reference ? [{ label: 'Reference', value: order.reference }] : []),
    ],
    party: { heading: 'Customer', name: order.contact.name, phone: order.contact.phone },
    lines: priceLines(order.lines, f),
    showPrices: true,
    totals: totalRows(order, f),
    notes: order.notes,
  };
}

export function deliveryDoc(delivery: Delivery, f: FormatSettings, orderNumber?: string): PrintDoc {
  return {
    title: 'Delivery note',
    number: delivery.number,
    status: delivery.status === 'REVERSED' ? 'Reversed' : undefined,
    meta: [
      { label: 'Date', value: businessDate(delivery.docDate, f) },
      { label: 'From', value: delivery.location.name },
      ...(orderNumber ? [{ label: 'Order', value: orderNumber }] : []),
    ],
    party: { heading: 'Deliver to', name: delivery.contact.name, phone: delivery.contact.phone },
    lines: delivery.lines.map((l) => ({
      description: l.description,
      sku: l.sku,
      qty: qty(l.qty, f),
      unit: l.unitSymbol,
    })),
    showPrices: false,
    totals: [],
    notes: delivery.notes,
  };
}

/** The customer's balance on a receipt: what they owe now, or what they have in credit. */
export function balanceRow(balance: string, f: FormatSettings): PrintRow {
  const b = dec(balance);
  return b < 0n
    ? { label: 'Account balance (credit)', value: money(toFixed(-b, f.currencyDecimals), f) }
    : { label: 'Account balance', value: money(balance, f) };
}

/** Thermal receipt for a counter sale (or any invoice, with the payment taken). */
export function receiptData(
  sale: Pick<CounterSale, 'invoice' | 'payment' | 'changeDue'>,
  f: FormatSettings,
  opts: {
    cashier?: string | null;
    tendered?: string | null;
    walkIn?: boolean;
    /** The customer's account balance after this sale, when known. */
    balance?: string | null;
  },
): ReceiptData {
  const inv = sale.invoice;
  const totals = totalRows(inv, f);
  const payments: PrintRow[] = [];
  if (sale.payment) {
    payments.push({
      label: sale.payment.method.name,
      value: money(
        opts.tendered ??
          toFixed(dec(sale.payment.amount) + dec(sale.changeDue), f.currencyDecimals),
        f,
      ),
    });
    if (dec(sale.changeDue) > 0n)
      payments.push({ label: 'Change', value: money(sale.changeDue, f), strong: true });
  } else if (dec(inv.amountPaid) > 0n) {
    payments.push({ label: 'Paid', value: money(inv.amountPaid, f) });
  }
  if (dec(inv.outstanding) > 0n)
    payments.push({ label: 'On account', value: money(inv.outstanding, f), strong: true });
  if (!opts.walkIn && opts.balance) payments.push(balanceRow(opts.balance, f));
  return {
    number: inv.number,
    dateTime: dateTime(inv.postedAt ?? new Date().toISOString(), f),
    customer: opts.walkIn ? null : inv.contact.name,
    cashier: opts.cashier,
    lines: inv.lines.map((l) => {
      const discount = dec(l.discountAmount) > 0n ? ` − ${money(l.discountAmount, f)}` : '';
      return {
        name: l.description,
        detail: `${qty(l.qty, f)} ${l.unitSymbol} × ${money(l.unitPrice, f)}${discount}`,
        amount: money(lineAmount(l, f.currencyDecimals), f),
      };
    }),
    totals,
    payments,
  };
}
